/* Master Weekly Card — screen + print layout modeled on the prototype:
   Day & Time · Medication & Purpose · My Pill Looks Like · Do Not Confuse With · My Information */
import React from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const DAY_SHORT = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const PURPOSE_COLORS = ["#2B5339", "#B0532C", "#7A5C99", "#1F5F8B", "#8A6D1F", "#3E7C5B", "#A0463B"];

const timeLabel = (times = []) =>
  times.length ? times.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(" · ") : "As directed";

export const MasterWeeklyCard = ({ profile, qr }) => {
  const meds = profile.meds;
  return (
    <div data-testid="master-weekly-card" className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden print:border-0 print:rounded-none med-master-card">
      {/* Header band */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-[#E5E2DC]">
        <div className="bg-[#1C2B23] text-white p-5">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#9BB8A5] font-semibold">
            Centennial Medication Safety Card™
          </div>
          <div className="med-h2 text-2xl font-medium mt-1 uppercase">
            {profile.name}&apos;s {meds.length} medication{meds.length === 1 ? "" : "s"} a week
          </div>
          <div className="text-[11px] text-[#9BB8A5] mt-1">Know It · Check It · Take It Safely</div>
        </div>
        <div className="p-5 border-t md:border-t-0 md:border-l border-[#E5E2DC]">
          <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#2B5339] mb-1 inline-flex items-center gap-1.5">
            <ShieldCheck size={11} /> 5-Way Safety Check — always check all 5
          </div>
          <ol className="text-xs text-[#1C2B23] space-y-0.5 list-decimal list-inside">
            <li>Medication name</li>
            <li>Strength</li>
            <li>Imprint (front &amp; back)</li>
            <li>Dosage form</li>
            <li>Prescription label &amp; manufacturer</li>
          </ol>
        </div>
        <div className="p-5 bg-[#FFF8E7] border-t md:border-t-0 md:border-l border-[#E5E2DC]">
          <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#8A6D1F] mb-1 inline-flex items-center gap-1.5">
            <AlertTriangle size={11} /> Important reminder
          </div>
          <p className="text-xs text-[#1C2B23] leading-relaxed">
            Never use color or shape alone to identify a medication.{" "}
            <span className="text-[#A0463B] font-semibold">Always check your label.</span> If you
            are unsure, ask your pharmacist.
          </p>
        </div>
      </div>

      {/* Column headers */}
      <div className="hidden md:grid med-master-grid text-[10px] tracking-[0.14em] uppercase font-semibold">
        <div className="bg-[#1F3B5C] text-white px-3 py-2">Day &amp; time</div>
        <div className="bg-[#1F3B5C] text-white px-3 py-2">Medication &amp; purpose</div>
        <div className="bg-[#1F3B5C] text-white px-3 py-2">My pill looks like <span className="normal-case font-normal">(yours may differ)</span></div>
        <div className="bg-[#A02E2E] text-white px-3 py-2">Do not confuse with</div>
        <div className="bg-[#1F3B5C] text-white px-3 py-2">My information</div>
      </div>

      {/* Rows */}
      {meds.map((m, i) => {
        const color = PURPOSE_COLORS[i % PURPOSE_COLORS.length];
        return (
          <div
            key={m.id}
            data-testid={`master-row-${i}`}
            className={`med-master-row border-t border-[#E5E2DC] ${i % 2 ? "bg-[#FBFAF8]" : "bg-white"}`}
          >
            <div className="px-3 py-3 flex md:block items-center gap-3">
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white text-sm font-semibold"
                style={{ background: color }}
              >
                {i + 1}
              </span>
              <div className="md:mt-1">
                <div className="text-xs font-semibold text-[#1C2B23]">{DAYS[i % 7]}</div>
                <div className="text-[11px] text-[#5C6B62]">{timeLabel(m.times)}</div>
              </div>
            </div>
            <div className="px-3 py-3">
              <div className="med-row-name text-sm font-semibold" style={{ color }}>
                {(m.generic_name || "").toUpperCase()} {m.strength}
              </div>
              {m.brand_name && (
                <div className="text-[11px] text-[#5C6B62]">Brand: {m.brand_name}</div>
              )}
              {m.purpose && (
                <div
                  className="inline-block text-[10px] font-semibold uppercase tracking-wide mt-1 px-2 py-0.5 rounded"
                  style={{ background: `${color}18`, color }}
                >
                  {m.purpose}
                </div>
              )}
              {m.directions && (
                <div className="text-[11px] text-[#1C2B23] mt-1">{m.directions}</div>
              )}
            </div>
            <div className="px-3 py-3 text-[11px] text-[#1C2B23]">
              {m.photo && (
                <img
                  src={m.photo}
                  alt="Reference: labeled container"
                  className="w-14 h-14 object-cover rounded border border-[#E5E2DC] float-right ml-2"
                />
              )}
              <div>Form: <span className="font-medium capitalize">{m.form || "—"}</span></div>
              <div>Imprint front: <span className="font-medium">{m.imprint_front || "—"}</span></div>
              <div>Imprint back: <span className="font-medium">{m.imprint_back || "(blank)"}</span></div>
              <div className="text-[10px] text-[#5C6B62] mt-1 italic">
                Example only — your pill may look different.
              </div>
            </div>
            <div className="px-3 py-3">
              {m.do_not_confuse ? (
                <div className="text-[11px]">
                  <div className="inline-flex items-center gap-1 text-[#A02E2E] font-semibold uppercase text-[10px] tracking-wide">
                    <AlertTriangle size={10} /> Look-alike
                  </div>
                  <div className="text-[#A02E2E] font-medium mt-0.5">{m.do_not_confuse}</div>
                  <div className="text-[10px] text-[#5C6B62]">Different medicine — check the name letter by letter.</div>
                </div>
              ) : (
                <div className="text-[11px] text-[#5C6B62]">—</div>
              )}
            </div>
            <div className="px-3 py-3 text-[11px] text-[#1C2B23] space-y-1.5">
              <div className="flex gap-1">My strength: <span className="flex-1 border-b border-dotted border-[#9AA79E]">{m.strength || ""}</span></div>
              <div className="flex gap-1">My imprint: <span className="flex-1 border-b border-dotted border-[#9AA79E]"></span></div>
              <div className="flex gap-1">My pharmacy: <span className="flex-1 border-b border-dotted border-[#9AA79E]">{m.pharmacy || ""}</span></div>
              <div className="flex gap-1">My manufacturer: <span className="flex-1 border-b border-dotted border-[#9AA79E]">{m.manufacturer || ""}</span></div>
            </div>
          </div>
        );
      })}

      {/* Footer band */}
      <div className={`grid grid-cols-1 ${qr ? "md:grid-cols-5" : "md:grid-cols-4"} border-t border-[#E5E2DC]`}>
        <div className="p-4 bg-[#FFF8E7]">
          <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-[#8A6D1F] mb-1">
            What to do if unsure
          </div>
          <ol className="text-[11px] text-[#1C2B23] list-decimal list-inside space-y-0.5">
            <li>Do NOT take the pill.</li>
            <li>Look at your prescription bottle.</li>
            <li>Compare all 5 safety checks.</li>
            <li>Call your pharmacist.</li>
            <li>When in doubt, don&apos;t take it out!</li>
          </ol>
        </div>
        <div className="p-4 border-t md:border-t-0 md:border-l border-[#E5E2DC]">
          <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-[#2B5339] mb-2">
            My weekly at-a-glance
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {DAY_SHORT.map((d, i) => (
              <div key={d} className="text-center">
                <div className="text-[9px] font-semibold text-[#5C6B62]">{d}</div>
                <div
                  className="w-6 h-6 rounded border-2 mt-0.5"
                  style={{ borderColor: PURPOSE_COLORS[i % PURPOSE_COLORS.length] }}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="p-4 bg-[#FDF2ED] border-t md:border-t-0 md:border-l border-[#E5E2DC]">
          <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-[#A0463B] mb-1">
            Emergency contacts
          </div>
          <div className="text-[11px] text-[#1C2B23] space-y-1.5">
            <div className="flex gap-1">Pharmacy: <span className="flex-1 border-b border-dotted border-[#C9A9A0]"></span></div>
            <div className="flex gap-1">Doctor: <span className="flex-1 border-b border-dotted border-[#C9A9A0]"></span></div>
            <div className="flex gap-1">Phone: <span className="flex-1 border-b border-dotted border-[#C9A9A0]"></span></div>
            <div className="font-semibold">In case of emergency: dial 911</div>
          </div>
        </div>
        <div className="p-4 border-t md:border-t-0 md:border-l border-[#E5E2DC]">
          <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-[#2B5339] mb-1">
            Notes for {profile.name} or caregivers
          </div>
          <div className="space-y-2.5 mt-2">
            <div className="border-b border-dotted border-[#9AA79E]"></div>
            <div className="border-b border-dotted border-[#9AA79E]"></div>
            <div className="border-b border-dotted border-[#9AA79E]"></div>
          </div>
        </div>
        {qr && (
          <div
            data-testid="master-card-qr"
            className="p-4 border-t md:border-t-0 md:border-l border-[#E5E2DC] text-center"
          >
            <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-[#2B5339] mb-1.5">
              Scan to open this card
            </div>
            <img
              src={qr}
              alt="QR code — opens this medication card on any phone"
              className="w-20 h-20 mx-auto"
            />
            <div className="text-[9px] text-[#5C6B62] mt-1.5 leading-tight">
              Works on any phone camera — for family, caregivers &amp; ER staff.
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#1C2B23] text-[#9BB8A5] text-[10px] px-4 py-2 flex flex-wrap justify-between gap-2">
        <span>Keep this card with your medications. Show it to your doctor and pharmacist at every visit.</span>
        <span>Educational use only — not a substitute for professional medical advice.</span>
      </div>

      <style>{`
        .med-master-grid { grid-template-columns: 1.1fr 1.7fr 1.4fr 1.3fr 1.5fr; }
        .med-master-row { display: block; }
        @media (min-width: 768px) {
          .med-master-row { display: grid; grid-template-columns: 1.1fr 1.7fr 1.4fr 1.3fr 1.5fr; }
        }
        @media print {
          .med-master-card { font-size: 10px; break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default MasterWeeklyCard;
